// API routes
export const API_ROUTES = {
    AUTH: {
        LOGIN: '/v1/auth/login',
        LOGOUT: '/v1/auth/logout',
        REGISTER: '/v1/auth/register',
        REFRESH_TOKEN: '/v1/auth/refresh-token',
        FORGOT_PASSWORD: '/v1/auth/forgot-password',
        RESET_PASSWORD: '/v1/auth/reset-password',
    },
    USER: {
        PROFILE: '/v1/users/me',
        UPDATE_PROFILE: '/v1/users/me',
        CHANGE_PASSWORD: '/v1/users/me/password',
        LIST: '/v1/users',
        DETAILS: (userId) => `/v1/users/${userId}`,
        // Right to be forgotten: a player erases their own account (DELETE), or a
        // Super Admin erases any user's account (DELETE on DETAILS path).
        DELETE_ACCOUNT: '/v1/users/me',
        ERASE: (userId) => `/v1/users/${userId}`,
    },
    TEAM: {
        CREATE: '/v1/teams',
        LIST: '/v1/teams',
        PENDING: '/v1/teams/pending',
        MY_TEAMS: '/v1/teams/me',
        // Lightweight team-creation flow
        SINGLES: '/v1/teams/singles',
        PAIR: '/v1/teams/pair',
        PARTNER_SEARCH: '/v1/teams/partners/search',
        PARTNER_REQUESTS: '/v1/teams/partner-requests',
        PARTNER_ACCEPT: (teamId) => `/v1/teams/partner-requests/${teamId}/accept`,
        PARTNER_DECLINE: (teamId) => `/v1/teams/partner-requests/${teamId}/decline`,
        RENAME: (teamId) => `/v1/teams/${teamId}/name`,
        ASSIGN: (teamId) => `/v1/teams/${teamId}/assignment`,
        UNASSIGN: (teamId) => `/v1/teams/${teamId}/assignment`,
        JOIN: (teamId) => `/v1/teams/${teamId}/join`,
        LEAVE: (teamId) => `/v1/teams/${teamId}/leave`,
        DETAILS: (teamId) => `/v1/teams/${teamId}`,
        UPDATE: (teamId) => `/v1/teams/${teamId}`,
        // DELETE maps to WITHDRAW (soft, status=withdrawn) — kept for backward compat.
        DELETE: (teamId) => `/v1/teams/${teamId}`,
        // Permanent deletion: hard-delete a team with no history, or soft-delete
        // (status=deleted) a team that has games/enrollments. Distinct from withdraw.
        DELETE_PERMANENT: (teamId) => `/v1/teams/${teamId}/permanent`,
        REVIEW: (teamId) => `/v1/teams/${teamId}/review`,
        MEMBERS: (teamId) => `/v1/teams/${teamId}/members`,
        TRANSFER_CAPTAIN: (teamId) => `/v1/teams/${teamId}/captain`,
        // Enrollment (self-service: enrol a team into a division ladder; list a team's enrollments)
        ENROLLMENTS: (teamId) => `/v1/teams/${teamId}/enrollments`,
    },
    ENROLLMENT: {
        WITHDRAW: (enrollmentId) => `/v1/enrollments/${enrollmentId}`,
        PLACE: (enrollmentId) => `/v1/enrollments/${enrollmentId}/placement`,
    },
    LEAGUE: {
        CREATE: '/v1/leagues',
        LIST: '/v1/leagues',
        DETAILS: (leagueId) => `/v1/leagues/${leagueId}`,
        UPDATE: (leagueId) => `/v1/leagues/${leagueId}`,
        DELETE: (leagueId) => `/v1/leagues/${leagueId}`,
        SEASONS: (leagueId) => `/v1/leagues/${leagueId}/seasons`,
        SEASON_DETAILS: (leagueId, seasonId) => `/v1/leagues/${leagueId}/seasons/${seasonId}`,
        SEASON_SUBSCRIBERS: (leagueId, seasonId) => `/v1/leagues/${leagueId}/seasons/${seasonId}/subscribers`,
        SEASON_ENROLLMENTS: (leagueId, seasonId) => `/v1/leagues/${leagueId}/seasons/${seasonId}/enrollments`,
        /** Teams queued against divisions, awaiting the next upcoming season (admin). */
        QUEUE: (leagueId) => `/v1/leagues/${leagueId}/queue`,
        DIVISIONS: (leagueId) => `/v1/leagues/${leagueId}/divisions`,
        ADMINISTRATORS: (leagueId) => `/v1/leagues/${leagueId}/administrators`,
        ADMINISTRATOR: (leagueId, userId) => `/v1/leagues/${leagueId}/administrators/${userId}`,
    },
    GAME: {
        CREATE: '/v1/games',
        RECORD: '/v1/games/record',
        FIXTURES_AVAILABLE: '/v1/games/fixtures/available',
        LIST: '/v1/games',
        DETAILS: (gameId) => `/v1/games/${gameId}`,
        UPDATE: (gameId) => `/v1/games/${gameId}`,
        ADMIN_UPDATE: (gameId) => `/v1/games/${gameId}/admin`,
        CANCEL: (gameId) => `/v1/games/${gameId}/cancel`,
        PROPOSALS: '/v1/games/proposals',
        PROPOSAL_ACCEPT: (proposalId) => `/v1/games/proposals/${proposalId}/accept`,
        PROPOSAL_DECLINE: (proposalId) => `/v1/games/proposals/${proposalId}/decline`,
        PROPOSAL_COUNTER: (proposalId) => `/v1/games/proposals/${proposalId}/counter`,
        SUBMIT_RESULT: (gameId) => `/v1/games/${gameId}/results`,
        APPROVE_RESULT: (gameId) => `/v1/games/${gameId}/results/approve`,
        APPROVE_RESULT_ON_BEHALF: (gameId) => `/v1/games/${gameId}/results/approve-on-behalf`,
        RESET_RESULT: (gameId) => `/v1/games/${gameId}/results/reset`,
        RESULT_STATUS: (gameId) => `/v1/games/${gameId}/results/status`,
        DISPUTE: (gameId) => `/v1/games/${gameId}/results/dispute`,
        RESOLVE: (gameId) => `/v1/games/${gameId}/results/resolve`,
    },
    ADMIN: {
        HEALTH: '/v1/admin/health',
        HEALTH_DB: '/v1/admin/health/db',
        LOGS: '/v1/admin/logs',
        LOG_DETAIL: (logId) => `/v1/admin/logs/${logId}`,
        LOGS_EXPORT: '/v1/admin/logs/export',
        LOGS_PURGE: '/v1/admin/logs/purge',
        LOGS_RETENTION: '/v1/admin/logs/retention',
        AUDIT: '/v1/admin/audit',
        AUDIT_DETAIL: (auditId) => `/v1/admin/audit/${auditId}`,
        AUDIT_BY_USER: (userId) => `/v1/admin/audit/user/${userId}`,
        AUDIT_BY_RESOURCE: (type, id) => `/v1/admin/audit/resource/${type}/${id}`,
        AUDIT_EXPORT: '/v1/admin/audit/export',
        METRICS: '/v1/admin/metrics',
        METRICS_LATEST: '/v1/admin/metrics/latest',
        METRIC_DETAIL: (metricId) => `/v1/admin/metrics/${metricId}`,
        STATUS: '/v1/admin/status',
        STATUS_SERVICES: '/v1/admin/status/services',
    },
};
// Validation constants
export const VALIDATION = {
    PASSWORD: {
        MIN_LENGTH: 8,
        MAX_LENGTH: 64,
        PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,64}$/,
        ERROR_MESSAGE: 'Password must be 8-64 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character.',
    },
    USERNAME: {
        MIN_LENGTH: 3,
        MAX_LENGTH: 30,
        PATTERN: /^[a-zA-Z0-9_-]{3,30}$/,
        ERROR_MESSAGE: 'Username must be 3-30 characters and can only contain letters, numbers, underscores, and hyphens.',
    },
    EMAIL: {
        PATTERN: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        ERROR_MESSAGE: 'Please enter a valid email address.',
    },
    BIRTH_YEAR: {
        MIN: 1920,
        MAX: new Date().getFullYear() - 10,
        ERROR_MESSAGE: 'Birth year must be between 1920 and the current year minus 10.',
    },
};
// Error codes
export const ERROR_CODES = {
    AUTH: {
        INVALID_CREDENTIALS: 'auth/invalid-credentials',
        EMAIL_IN_USE: 'auth/email-in-use',
        WEAK_PASSWORD: 'auth/weak-password',
        EXPIRED_TOKEN: 'auth/expired-token',
        INVALID_TOKEN: 'auth/invalid-token',
        USER_NOT_FOUND: 'auth/user-not-found',
        UNAUTHORIZED: 'auth/unauthorized',
    },
    VALIDATION: {
        INVALID_INPUT: 'validation/invalid-input',
        MISSING_FIELD: 'validation/missing-field',
        INVALID_FORMAT: 'validation/invalid-format',
    },
    SERVER: {
        INTERNAL_ERROR: 'server/internal-error',
        NOT_IMPLEMENTED: 'server/not-implemented',
        SERVICE_UNAVAILABLE: 'server/service-unavailable',
    },
    RESOURCE: {
        NOT_FOUND: 'resource/not-found',
        ALREADY_EXISTS: 'resource/already-exists',
        CONFLICT: 'resource/conflict',
    },
    TEAM: {
        INVALID_COMPOSITION: 'team/invalid-composition',
        PLAYER_ALREADY_IN_TEAM: 'team/player-already-in-team',
        TEAM_FULL: 'team/team-full',
        INVALID_TYPE: 'team/invalid-type',
        GENDER_REQUIREMENT_NOT_MET: 'team/gender-requirement-not-met',
        INVALID_REVIEW_DECISION: 'team/invalid-review-decision',
        ALREADY_REVIEWED: 'team/already-reviewed',
        // Lightweight team-creation / partner flow
        PARTNER_NOT_REGISTERED: 'team/partner-not-registered',
        PARTNER_GENDER_INVALID: 'team/partner-gender-invalid',
        PARTNER_SELF: 'team/partner-self',
        PARTNER_REQUEST_NOT_PENDING: 'team/partner-request-not-pending',
        NOT_TEAM_OWNER: 'team/not-team-owner',
        INVALID_TEAM_STATE: 'team/invalid-team-state',
        // Captaincy transfer
        CAPTAIN_NOT_MEMBER: 'team/captain-not-member',
        // League join / category entry
        ALREADY_JOINED_LEAGUE: 'team/already-joined-league',
        REGISTRATION_CLOSED: 'team/registration-closed',
    },
    GAME: {
        INVALID_SCHEDULE: 'game/invalid-schedule',
        INVALID_RESULT: 'game/invalid-result',
        ALREADY_COMPLETED: 'game/already-completed',
        VERIFICATION_REQUIRED: 'game/verification-required',
        TEAMS_NOT_SAME_DIVISION: 'game/teams-not-same-division',
        RESULT_ALREADY_SUBMITTED: 'game/result-already-submitted',
        DISPUTE_DEADLINE_PASSED: 'game/dispute-deadline-passed',
        GAME_CANCELLED: 'game/game-cancelled',
        NOT_GAME_PARTICIPANT: 'game/not-game-participant',
        PROPOSAL_EXPIRED: 'game/proposal-expired',
        SCHEDULING_CONFLICT: 'game/scheduling-conflict',
        GAME_NOT_FOUND: 'game/game-not-found',
        PROPOSAL_NOT_FOUND: 'game/proposal-not-found',
        NOT_PROPOSAL_PARTICIPANT: 'game/not-proposal-participant',
        ALREADY_CLAIMED: 'game/already-claimed',
        SEASON_COMPLETED: 'game/season-completed',
        HAS_ACTIVE_PROPOSAL: 'game/has-active-proposal',
        DATE_OUTSIDE_SEASON: 'game/date-outside-season',
    },
    ADMIN: {
        LOG_ACCESS_DENIED: 'admin/log-access-denied',
        LOG_NOT_FOUND: 'admin/log-not-found',
        INVALID_LOG_QUERY: 'admin/invalid-log-query',
        LOG_EXPORT_FAILED: 'admin/log-export-failed',
        LOG_RETENTION_INVALID: 'admin/log-retention-invalid',
        PURGE_CONFIRMATION_REQUIRED: 'admin/purge-confirmation-required',
        METRIC_NOT_FOUND: 'admin/metric-not-found',
    },
};
// Local storage keys
export const STORAGE_KEYS = {
    ACCESS_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken',
    USER: 'user',
    THEME: 'theme',
    LANGUAGE: 'language',
};
// Date formats
export const DATE_FORMATS = {
    SHORT_DATE: 'MM/DD/YYYY',
    LONG_DATE: 'MMMM D, YYYY',
    TIME: 'h:mm A',
    DATETIME: 'MMMM D, YYYY h:mm A',
    ISO: 'YYYY-MM-DD',
    ISO_DATETIME: 'YYYY-MM-DDTHH:mm:ss.sssZ',
};
// App settings
export const APP_SETTINGS = {
    DEFAULT_PAGINATION_LIMIT: 20,
    MAX_PAGINATION_LIMIT: 100,
    TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes in milliseconds
    MAX_FILE_UPLOAD_SIZE: 5 * 1024 * 1024, // 5MB in bytes
    ACCEPTED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif'],
    MAX_IMAGE_DIMENSION: 2048, // pixels
};
